import type { ReactNode } from "react";
import { ArrowDownRight, ArrowRight, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/cn";

type Trend = "up" | "down" | "flat";

const trendMeta: Record<Trend, { Icon: typeof ArrowUpRight; cls: string; label: string }> = {
  up: { Icon: ArrowUpRight, cls: "text-routine-text", label: "naik" },
  down: { Icon: ArrowDownRight, cls: "text-critical-text", label: "turun" },
  flat: { Icon: ArrowRight, cls: "text-content-subtle", label: "stabil" },
};

/**
 * The KPI tile. Label small and quiet, value large — that size contrast is
 * doing most of the work, so the value never competes with its own caption.
 */
export function StatTile({
  label,
  value,
  unit,
  caption,
  trend,
  trendValue,
  icon,
  className,
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  caption?: string;
  trend?: Trend;
  trendValue?: string;
  icon?: ReactNode;
  className?: string;
}) {
  const meta = trend ? trendMeta[trend] : null;
  return (
    <div
      className={cn(
        "bg-surface border-hairline shadow-raise rounded-(--radius-card) border p-4",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="eyebrow">{label}</p>
        {icon && <span className="text-content-subtle shrink-0">{icon}</span>}
      </div>

      <p className="tabular mt-2 font-display text-3xl leading-none font-extrabold">
        {value}
        {unit && (
          <span className="text-content-subtle ml-1 text-base font-semibold">{unit}</span>
        )}
      </p>

      <div className="mt-2 flex items-center gap-1.5">
        {meta && trendValue && (
          <span className={cn("inline-flex items-center gap-0.5 text-xs font-bold", meta.cls)}>
            <meta.Icon aria-hidden className="size-3.5" />
            <span className="sr-only">{meta.label} </span>
            {trendValue}
          </span>
        )}
        {caption && <span className="text-content-subtle text-xs">{caption}</span>}
      </div>
    </div>
  );
}
